import { useEffect, useState } from "react";
import Warranty from "src/components/legals/Warranty";
import { cn } from "@/libs/utils";
import Download from "src/components/legals/Download";
import { Portal } from "src/components/portal/Portal";
import SelectLegalTerm from "src/components/legals/SelecteLegalTerm";
import { useTrans } from "src/utils/useTrans";
import { useIsMobileView } from "@/contexts/ResponsiveProvider";
import { LegalDocumentId } from "@/models/Legal";
import { MDViewer } from "@/common/MDViewer";
import { useRouter } from "next/router";
import PageTemplate from "@/components/templates/PageTemplate";
import { serverSideTranslations } from "@/libs/i18n/serverSideTranslations";
import SafetyAndWarranty from "@/components/legals/SafetyAndWarranty";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import { getLegalDoc, type LegalDocResolved } from "@/libs/legals";

const Options: { id: string; label: string }[] = [
  {
    id: "health-and-safety-warnings",
    label: "Health and Safety",
  },
  { id: "warranty", label: "Warranty" },
  {
    id: LegalDocumentId.CookiePolicy,
    label: "Cookies Policy",
  },
  {
    id: LegalDocumentId.Eula,
    label: "EULA",
  },
];

const Label = ({ on, label }: { on: boolean; label: string }) => (
  <h4
    className={cn(
      !on && "opacity-50",
      "block cursor-pointer text-sm font-700 md:pb-4",
    )}
  >
    {label}
  </h4>
);

const Head = ({ text, className }: { text: string; className?: string }) => (
  <h2
    className={cn(
      "text-1.5xl font-700 leading-tight md:text-2xl lg:text-3xl",
      className,
    )}
  >
    {text}
  </h2>
);

const Line = ({ className = "" }) => (
  <span className={cn("h-px w-full", className)} />
);

const OptionRow = ({
  selected,
  option,
  onOpen = () => {},
  onSelect,
}: {
  selected: boolean;
  onOpen?: (e: any) => void;
  onSelect: (e: any) => void;
  option: { id: string; label: string };
}) => {
  const { t } = useTrans(["gnb"]);

  return (
    <div>
      <div onClick={onSelect} className={"hidden items-center md:flex"}>
        <Label on={selected} label={t(option.label)} />
      </div>
      {/* 모바일에서만 활성화 */}
      <div
        onClick={onOpen}
        className={cn(
          selected ? "flex md:hidden" : "hidden",
          "cursor-pointer items-center",
        )}
      >
        <Label on={selected} label={t(option.label)} />
        <ChevronDownIcon />
      </div>
    </div>
  );
};

const Mobile = ({ id, doc }: { id: string; doc: LegalDocResolved | null }) => {
  const router = useRouter();
  const { t } = useTrans(["gnb"]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflowY = open ? "hidden" : "";
  }, [open]);

  return (
    <>
      {open && (
        <Portal className={"fixed inset-0 z-50 bg-white"}>
          <SelectLegalTerm
            onClose={(id) => {
              setOpen(false);
              router.push("/legals/?id=" + id);
            }}
            selectedOption={id}
            options={Options}
          />
        </Portal>
      )}
      <div className={cn("mx-auto w-full bg-white text-[#222222] sm:pt-20")}>
        <div className={"flex flex-col px-5 pt-7.5"}>
          <div className={cn("flex w-full items-center justify-between")}>
            <Head text={t("Legal Terms")} />
            <div className={"flex flex-col"}>
              {Options.map((option, index) => {
                const selected = id === option.id;
                return (
                  <OptionRow
                    onOpen={() => setOpen(true)}
                    key={index}
                    selected={selected}
                    onSelect={() => router.push("/legals/" + option.id)}
                    option={option}
                  />
                );
              })}
            </div>
          </div>
          <div className={"flex w-full flex-col"}>
            {id === "warranty" && <Warranty />}
            {id === "health-and-safety-warnings" && <SafetyAndWarranty />}
            {!!doc && (
              <div className={"mt-8"}>
                {!!doc.pdfUrl && (
                  <a target="_blank" rel="noreferrer" href={doc.pdfUrl}>
                    <Download />
                  </a>
                )}
                <MDViewer md={doc.contents} isError={false} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Desktop = ({ id, doc }: { id: string; doc: LegalDocResolved | null }) => {
  const router = useRouter();
  const { t } = useTrans(["gnb"]);

  return (
    <>
      <div
        className={cn(
          "mx-auto w-full max-w-[1234px] bg-white pt-20 text-[#222222]",
        )}
      >
        <div className={"flex flex-row px-5 pt-0"}>
          <div
            className={cn(
              "flex w-full flex-col items-start justify-start",
              "max-w-[378px]",
            )}
          >
            <Head text={t("Legal Terms")} />
            <Line className={"mt-2.5 block bg-[#222222]"} />
            <div className={"flex flex-col pt-12"}>
              {Options.map((_option, index) => {
                const selected = id === _option.id;
                return (
                  <OptionRow
                    key={index}
                    selected={selected}
                    onSelect={() => router.push("/legals/?id=" + _option.id)}
                    option={_option}
                  />
                );
              })}
            </div>
          </div>
          <div className={"ml-7.5 flex w-full max-w-screen-smd flex-col"}>
            <Head text={t("Legal Terms")} className={"invisible block"} />
            <Line className={"mt-2.5 block bg-[#e2e2e2]"} />
            {id === "warranty" && <Warranty />}
            {id === "health-and-safety-warnings" && <SafetyAndWarranty />}
            {!!doc && (
              <div className={"mt-8"}>
                {!!doc.pdfUrl && (
                  <a target="_blank" rel="noreferrer" href={doc.pdfUrl}>
                    <Download />
                  </a>
                )}
                <MDViewer md={doc.contents} isError={false} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Legals = ({ id, doc }: { id: string; doc: LegalDocResolved | null }) => {
  const mobile = useIsMobileView();

  return (
    <PageTemplate>
      <PageTemplate.Content>
        {mobile ? <Mobile id={id} doc={doc} /> : <Desktop id={id} doc={doc} />}
      </PageTemplate.Content>
    </PageTemplate>
  );
};

// 이 페이지는 `?id=` 쿼리로 문서를 고르므로 getStaticProps 를 쓸 수 없다.
// 문서 해석은 반드시 서버에서 — 컴포넌트에서 getLegalDoc 을 부르면 빌드 캐시
// 전체(수백 KB)가 클라이언트 번들에 실린다.
export async function getServerSideProps({ locale, query }) {
  const id = (query?.id as string) || "health-and-safety-warnings";
  return {
    props: {
      id,
      doc: getLegalDoc(id, locale),
      ...(await serverSideTranslations(locale, ["support", "index", "gnb"])),
    },
  };
}

export default Legals;
